import type {
  RouteLeg,
  RouteMapPin,
  RoutePinState,
  RoutePlanStop,
  RouteStayLeg,
} from '@/types/route-plan';

export type RouteMapCoordinate = { latitude: number; longitude: number };

export type RouteMapRegion = {
  latitude: number;
  longitude: number;
  latitudeDelta: number;
  longitudeDelta: number;
};

export type RouteMapModel = {
  pins: RouteMapPin[];
  polylineCoordinates: RouteMapCoordinate[];
  unmappedStops: RoutePlanStop[];
};

type RouteMapBuildInput = {
  stops: RoutePlanStop[];
  legs: RouteLeg[];
  completedLegIds: string[];
  currentLegIndex: number; // -1 = no current step (wizard preview)
};

type PinEntry = {
  leg: RouteStayLeg;
  stop: RoutePlanStop;
  stepNumber: number;
  state: RoutePinState;
};

const MIN_REGION_DELTA = 0.01;
const REGION_PADDING_FACTOR = 1.4;

/**
 * Pure derivation of the map model from a route plan: one pin per mapped
 * stay leg (numbered like the home step pager), shoulder stacks for shared
 * coordinates, the connecting polyline, and the list of stops that have a
 * location but no coordinates. Never imports react-native-maps.
 */
export class RouteMapPinBuilder {
  static build(input: RouteMapBuildInput): RouteMapModel {
    const { stops, legs, completedLegIds, currentLegIndex } = input;
    const stopsById = new Map(stops.map((stop) => [stop.id, stop]));
    const completedSet = new Set(completedLegIds);
    const currentStayLegId = RouteMapPinBuilder.currentStayLegId(legs, currentLegIndex);

    const entries: PinEntry[] = [];
    const unmappedById = new Map<string, RoutePlanStop>();

    legs.forEach((leg, index) => {
      if (leg.kind !== 'stay') return;
      const stop = stopsById.get(leg.stopId);
      if (!stop) return;

      if (stop.latitude === undefined || stop.longitude === undefined) {
        if (stop.location?.trim()) unmappedById.set(stop.id, stop);
        return;
      }

      const state: RoutePinState =
        leg.id === currentStayLegId
          ? 'current'
          : completedSet.has(leg.id)
            ? 'done'
            : 'upcoming';
      entries.push({ leg, stop, stepNumber: index + 1, state });
    });

    return {
      pins: RouteMapPinBuilder.stackEntries(entries),
      polylineCoordinates: entries.map((entry) => ({
        latitude: entry.stop.latitude as number,
        longitude: entry.stop.longitude as number,
      })),
      unmappedStops: [...unmappedById.values()],
    };
  }

  /**
   * Bounding region for a set of coordinates, padded, with a sane minimum
   * span. Null when there is nothing to show.
   */
  static regionFor(coordinates: RouteMapCoordinate[]): RouteMapRegion | null {
    if (coordinates.length === 0) return null;
    const latitudes = coordinates.map((coordinate) => coordinate.latitude);
    const longitudes = coordinates.map((coordinate) => coordinate.longitude);
    const minLat = Math.min(...latitudes);
    const maxLat = Math.max(...latitudes);
    const minLng = Math.min(...longitudes);
    const maxLng = Math.max(...longitudes);
    return {
      latitude: (minLat + maxLat) / 2,
      longitude: (minLng + maxLng) / 2,
      latitudeDelta: Math.max((maxLat - minLat) * REGION_PADDING_FACTOR, MIN_REGION_DELTA),
      longitudeDelta: Math.max((maxLng - minLng) * REGION_PADDING_FACTOR, MIN_REGION_DELTA),
    };
  }

  /**
   * The stay leg whose pin carries the "current" highlight: the current leg
   * itself when it is a stay; when the user is mid-travel, the destination
   * (the next stay leg).
   */
  private static currentStayLegId(legs: RouteLeg[], currentLegIndex: number): string | null {
    if (currentLegIndex < 0) return null;
    for (let index = currentLegIndex; index < legs.length; index += 1) {
      if (legs[index].kind === 'stay') return legs[index].id;
    }
    return null;
  }

  /**
   * Group entries by rounded coordinate into shoulder stacks. Front pin
   * (stackOrder 0) = current step there, else next incomplete, else last;
   * the rest keep step order behind it.
   */
  private static stackEntries(entries: PinEntry[]): RouteMapPin[] {
    const stacks = new Map<string, PinEntry[]>();
    for (const entry of entries) {
      const key = `${(entry.stop.latitude as number).toFixed(5)},${(entry.stop.longitude as number).toFixed(5)}`;
      const group = stacks.get(key);
      if (group) {
        group.push(entry);
      } else {
        stacks.set(key, [entry]);
      }
    }

    const pins: RouteMapPin[] = [];
    for (const [stackKey, group] of stacks) {
      const front = RouteMapPinBuilder.frontEntry(group);
      const ordered = [front, ...group.filter((entry) => entry !== front)];
      ordered.forEach((entry, stackOrder) => {
        pins.push({
          id: entry.leg.id,
          latitude: entry.stop.latitude as number,
          longitude: entry.stop.longitude as number,
          stepNumber: entry.stepNumber,
          stopId: entry.stop.id,
          legId: entry.leg.id,
          title: entry.stop.title,
          timeLabel: entry.leg.arriveTime,
          state: entry.state,
          stackKey,
          stackOrder,
          stackSize: group.length,
        });
      });
    }
    return pins;
  }

  private static frontEntry(group: PinEntry[]): PinEntry {
    return (
      group.find((entry) => entry.state === 'current') ??
      group.find((entry) => entry.state === 'upcoming') ??
      group[group.length - 1]
    );
  }
}
